import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../hooks/NotificationContext';
import { getExpiry } from '../../lib/utils';
import { fetchAPIToken, generateAPIToken } from '../../modules/automation';
import ActionButton from '../../ui/primitives/ActionButton';
import CustomInputField from '../../ui/primitives/CustomInputField';
import Loader from '../elements/Loader';

const AutomationCredentials = () => {
  const [apiToken, setAPIToken] = useState(null);
  const [error, setError] = useState(null);
  const { addNotification } = useNotification();
  const [generatingToken, setGeneratingToken] = useState(false);
  const { projectId } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const getExistingToken = async () => {
    try {
      setLoading(true);
      const response = await fetchAPIToken(projectId);
      setAPIToken(response?.body || null);
      setData(response);
    } catch (error) {
      setError(error.message || 'Failed to fetch API token');
    } finally {
      setLoading(false);
    }
  };

  const generateNewToken = async () => {
    setGeneratingToken(true);
    try {
      await generateAPIToken(projectId);
      await getExistingToken();
      addNotification('success', 'API token generated successfully!');
    } catch (error) {
      addNotification('danger', 'Unable to generate API token');
    }
    setGeneratingToken(false);
  };

  useEffect(() => {
    getExistingToken();
  }, [projectId]);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(apiToken)
      .then(() => addNotification('success', 'Copied API token!'))
      .catch(() => addNotification('danger', 'Failed to copy token!'));
  };

  if (loading) return <Loader />;

  const isExpired = getExpiry(data?.created_at, data?.expires_in);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This token can be used to access our public APIs. It will be valid for 90 days.
          You can generate a new token at any time.
        </Typography>

        <ActionButton
          label={generatingToken ? 'Generating…' : 'Generate GAINSS API Token'}
          intent="primary"
          loading={generatingToken}
          disabled={generatingToken}
          onClick={generateNewToken}
        />

        {error && (
          <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}

        {!generatingToken && apiToken && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                Your API Token
                {isExpired && (
                  <Typography component="span" variant="caption" color="error.main" sx={{ ml: 1 }}>
                    (Expired)
                  </Typography>
                )}
              </Typography>
              <ActionButton
                label="Click To Copy"
                intent="ghost"
                size="small"
                onClick={handleCopy}
              />
            </Box>
            <CustomInputField
              id="api-token"
              value={apiToken}
              multiline
              rows={3}
              disabled
              onChange={() => {}}
            />
          </Box>
        )}
      </Box>
  );
};

export default AutomationCredentials;